import { createContext, useContext } from 'react';
import type { Catalog } from '@/features/intelligence/dashboard/catalog';
import type { DatasetBag, DatasetId } from '@/features/intelligence/dashboard/datasets';
import type { ForecastSettings } from '@/features/intelligence/dashboard/evaluate';
import type { DashboardWindow } from '@/features/intelligence/dashboard/range';
import type { DashboardPageKey } from '@/features/intelligence/dashboard/types';

export interface DashboardContextValue {
  page: DashboardPageKey;
  bag: DatasetBag;
  catalog: Catalog;
  window: DashboardWindow;
  forecast: ForecastSettings;
  pending: ReadonlySet<DatasetId>;
  errors: Partial<Record<DatasetId, string>>;
  editing: boolean;
  isMobile: boolean;
}

export const DashboardContext = createContext<DashboardContextValue | null>(null);

export function useDashboard(): DashboardContextValue {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error('useDashboard must be used inside a DashboardBoard');
  return ctx;
}

/** Loading / error state of the datasets a widget's sources depend on. */
export function useSourceState(sourceIds: string[]): { loading: boolean; error: string | null; missing: string[] } {
  const { catalog, pending, errors } = useDashboard();
  const missing: string[] = [];
  let loading = false;
  let error: string | null = null;
  for (const id of sourceIds) {
    const def = catalog.byId.get(id);
    if (!def) {
      missing.push(id);
      continue;
    }
    for (const ds of def.requires) {
      if (pending.has(ds)) loading = true;
      const err = errors[ds];
      if (err && !error) error = err;
    }
    if (def.kind === 'metric' && def.ratio) {
      for (const dep of [def.ratio.num, def.ratio.den]) {
        const depDef = catalog.metric(dep);
        if (!depDef) continue;
        for (const ds of depDef.requires) {
          if (pending.has(ds)) loading = true;
          const err = errors[ds];
          if (err && !error) error = err;
        }
      }
    }
  }
  return { loading, error, missing };
}
